// JAPAN WEB CLINIC (Dawn) — behavior shared by every page (layout/theme.liquid):
// - the shipping countdown in every [data-countdown] (owner F-14)
// - bottom bars [data-jwc-sticky]: shown after an element has scrolled past the header (data-show-after), hidden while
//   an element is on screen (data-hide-while), or switched off by a page script (data-off). data-hide-mode="away"
//   slides the bar down instead of removing it. The page keeps room for the bar (body.jwc-bar-on, --jwc-bar-h).
// - the first-visit language confirmation (snippets/jwc-lang-modal.liquid)
// - links to the Joy Loyalty window (#joy-home, #joy-referral-program: the menu and the cart); its floating button hidden
(() => {
  'use strict';
  const J = window.JWC;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  let settings = {};
  try {
    settings = JSON.parse($('#JwcSettings')?.textContent || '{}');
  } catch (e) {
    settings = {};
  }
  J.settings = settings;
  const T = J.texts(settings.t);
  J.setTestClock(settings.testNow);
  // ?jst=YYYY-MM-DDTHH:MM sets the clock for checking, on an unpublished theme (preview) only
  const role = window.Shopify?.theme?.role;
  const jst = new URLSearchParams(location.search).get('jst');
  if (jst && role && role !== 'main') J.setTestClock(jst);

  // ---------------------------------------------------------------- countdown
  function setupCountdown() {
    const els = $$('[data-countdown]');
    if (!els.length || !settings.countdown || !settings.schedule) return;
    const tick = () => {
      const c = J.countdown(settings.schedule);
      const t = c.minutes >= 60 ? J.fill(T.cd_hm, { h: c.hours, m: c.rest }) : J.fill(T.cd_m, { m: c.minutes });
      const text = c.key ? J.fill(c.key === 'today' ? T.cd_today : T.cd_tomorrow, { t: '\u0000', h: c.cutoffHour }) : '';
      els.forEach((el) => {
        el.hidden = !c.key;
        el.closest('[data-jwc-sticky]')?.classList.toggle('is-cd-warn', c.warn);
        // an element's own words (data-cd-today / data-cd-tomorrow with {t}), e.g. the certificate's bar: the short form
        const own = c.key === 'today' ? el.dataset.cdToday : el.dataset.cdTomorrow;
        const words = own ? J.fill(own, { t: '\u0000', h: c.cutoffHour }) : text;
        const key = `${words}|${t}|${c.warn}`;
        if (el.dataset.shown === key) return;
        el.dataset.shown = key;
        el.replaceChildren();
        if (!c.key) return;
        const line = document.createElement('span');
        const [before, after = ''] = words.split('\u0000');
        const strong = document.createElement('b');
        strong.textContent = t;
        line.append(before, strong, after);
        el.append(line);
        if (c.warn) {
          const warn = document.createElement('span');
          warn.className = 'jwc-cd__warn';
          warn.textContent = J.fill(T.cd_warn, { h: c.cutoffHour });
          el.append(warn);
        }
      });
      updateBars();
    };
    tick();
    setInterval(tick, 15000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tick();
    });
  }

  // ---------------------------------------------------------------- bottom bars
  const headerBottom = () => Math.max(0, $('.section-header')?.getBoundingClientRect().bottom || 0);
  const onScreen = (el) => {
    if (!el || !el.getClientRects().length) return false;
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  };
  let queued = false;
  function updateBars() {
    queued = false;
    let height = 0;
    $$('[data-jwc-sticky]').forEach((bar) => {
      const after = bar.dataset.showAfter ? $(bar.dataset.showAfter) : null;
      const passed = !bar.dataset.showAfter || (after && after.getBoundingClientRect().bottom < headerBottom());
      const blocked = bar.dataset.hideWhile && $$(bar.dataset.hideWhile).some(onScreen);
      const on = !bar.hasAttribute('data-off') && passed;
      if (bar.dataset.hideMode === 'away') {
        bar.hidden = !on;
        bar.classList.toggle('jwc-is-away', Boolean(blocked));
      } else {
        bar.hidden = !on || Boolean(blocked);
      }
      if (on) height = Math.max(height, bar.getBoundingClientRect().height);
    });
    document.body.classList.toggle('jwc-bar-on', height > 0);
    if (height > 0) document.body.style.setProperty('--jwc-bar-h', `${Math.ceil(height)}px`);
  }
  const queueBars = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(updateBars);
  };
  J.updateBars = queueBars;
  function setupBars() {
    if (!$('[data-jwc-sticky]')) return;
    window.addEventListener('scroll', queueBars, { passive: true });
    window.addEventListener('resize', queueBars);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(queueBars);
      $$('[data-jwc-sticky]').forEach((bar) => observer.observe(bar));
    }
    updateBars();
  }

  // ---------------------------------------------------------------- first-visit language
  const LANG_KEY = 'jwc-lang';
  const chosenLang = () => {
    for (const store of ['localStorage', 'sessionStorage']) {
      try {
        const value = window[store].getItem(LANG_KEY);
        if (value) return value;
      } catch (e) {
        /* storage blocked: try the next one */
      }
    }
    return null;
  };
  const keepLang = (code) => {
    try {
      localStorage.setItem(LANG_KEY, code);
    } catch (e) {
      try {
        sessionStorage.setItem(LANG_KEY, code);
      } catch (e2) {
        /* asked again next page */
      }
    }
  };
  function setupLanguage() {
    const modal = $('#JwcLanguageModal');
    if (!modal || typeof modal.show !== 'function') return;
    const current = modal.dataset.current;
    const codes = $$('[data-lang]', modal).map((b) => b.dataset.lang);
    // suggest the first browser language the store has (zh-TW for Taiwan / Hong Kong / Macau / Hant)
    const prefs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || 'en']).map((l) => l.toLowerCase());
    const pick = (l) => (l.startsWith('zh') ? (/(tw|hk|mo|hant)/.test(l) ? 'zh-TW' : 'zh-CN') : l.slice(0, 2));
    const suggest = prefs.map(pick).find((l) => codes.includes(l)) || (codes.includes('en') ? 'en' : current);
    $$('[data-lang]', modal).forEach((b) => {
      b.classList.toggle('button--primary', b.dataset.lang === suggest);
      b.classList.toggle('button--secondary', b.dataset.lang !== suggest);
      b.addEventListener('click', (event) => {
        keepLang(b.dataset.lang);
        if (b.dataset.lang === current) {
          event.preventDefault();
          modal.hide();
        }
        // another language: the localization form is submitted and the store opens the page in that language
      });
    });
    // closing (button, Escape, outside) keeps the language on screen
    new MutationObserver(() => {
      if (!modal.hasAttribute('open') && !chosenLang()) keepLang(current);
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    if (!chosenLang() && !window.Shopify?.designMode) setTimeout(() => modal.show(document.activeElement), 250);
  }

  // ---------------------------------------------------------------- Joy Loyalty links
  // The coupon / refer-a-friend window opens from links (owner 2026-10-22: the menu and the cart instead of the floating
  // button; Joy opens it on the URL's #joy-… and clears the hash when it closes). Stay on this page even when the link
  // carries another path, and close the phone menu drawer first so the window is not under it.
  function setupJoyLinks() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="#joy-"]');
      if (!link) return;
      const drawer = link.closest('header-drawer');
      // Dawn's header drawer only closes when given the element to focus afterwards (its menu button)
      if (drawer && typeof drawer.closeMenuDrawer === 'function') drawer.closeMenuDrawer(event, drawer.querySelector('summary'));
      if (link.pathname !== location.pathname) {
        event.preventDefault();
        location.hash = link.hash;
      }
    });
  }

  // The floating 「JWCクーポン」 button stays hidden (owner 2026-10-22: it covered the bars and cards; the links open the
  // window). Until the window is first opened it is a plain button (assets/jwc-look.css); once opened, Joy draws the
  // window and, after closing, the button again inside its own shadow root, out of the page's CSS reach — so a style
  // is added to that shadow root whenever Joy puts the window in its wrapper.
  function hideJoyButton() {
    if (!('adoptedStyleSheets' in Document.prototype)) return;
    const sheet = new CSSStyleSheet();
    sheet.replaceSync('joy-fab-button { display: none !important; }');
    const apply = () => {
      const root = document.querySelector('joy-loyalty-widget')?.shadowRoot;
      if (root && !root.adoptedStyleSheets.includes(sheet)) root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    };
    const watch = (wrap) => new MutationObserver(apply).observe(wrap, { childList: true });
    const wrap = document.getElementById('Avada-Joy_FloatingButton');
    if (wrap) watch(wrap);
    else {
      new MutationObserver((list, observer) => {
        const found = document.getElementById('Avada-Joy_FloatingButton');
        if (!found) return;
        observer.disconnect();
        watch(found);
        apply();
      }).observe(document.body, { childList: true });
    }
    apply();
  }

  function start() {
    setupCountdown();
    setupBars();
    setupLanguage();
    setupJoyLinks();
    hideJoyButton();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
